import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, Search, Eye, Copy, FileDown, ChevronLeft, ChevronRight, FileText } from 'lucide-react'
import toast from 'react-hot-toast'

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TableColumnVisibilityMenu } from '@/components/shared/TableColumnVisibilityMenu'
import { ListLoader } from '@/components/shared/ListLoader'
import type { InventoryMovementRow } from '../types'
import { inventoryMovementService } from '@/services/inventoryMovementService'

const PAGE_SIZE = 10

type ColumnKey = 'code' | 'movementDate' | 'remarks' | 'sourceWarehouse' | 'destinationWarehouse'

const ALL_COLUMNS: { key: ColumnKey; labelKey: string }[] = [
  { key: 'code', labelKey: 'inventoryMovements.columns.code' },
  { key: 'movementDate', labelKey: 'inventoryMovements.columns.movementDate' },
  { key: 'remarks', labelKey: 'inventoryMovements.columns.remarks' },
  { key: 'sourceWarehouse', labelKey: 'inventoryMovements.columns.sourceWarehouse' },
  { key: 'destinationWarehouse', labelKey: 'inventoryMovements.columns.destinationWarehouse' },
]

export const InventoryMovementList: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()

  const [rows, setRows] = useState<InventoryMovementRow[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [totalCount, setTotalCount] = useState(0)

  const [searchQuery, setSearchQuery] = useState('')
  const [visibleColumns, setVisibleColumns] = useState<Record<ColumnKey, boolean>>({
    code: true,
    movementDate: true,
    remarks: true,
    sourceWarehouse: true,
    destinationWarehouse: true,
  })

  const load = async (page: number) => {
    setLoading(true)
    try {
      const res = await inventoryMovementService.getAllInventoryTransfers(page, PAGE_SIZE)
      setRows(res.items)
      setTotalCount(res.totalCount)
    } catch (e: any) {
      // If backend is not connected yet, still show empty table.
      const msg = e?.response?.data?.message
      toast.error(msg || t('inventoryMovements.failedToLoad', { defaultValue: 'Failed to load' }))
      setRows([])
      setTotalCount(0)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load(pageNumber)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber])

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) =>
      [r.code, r.movementDate, r.remarks, r.sourceWarehouse, r.destinationWarehouse].some((f) =>
        String(f).toLowerCase().includes(q),
      ),
    )
  }, [rows, searchQuery])

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const page = Math.min(pageNumber, totalPages)

  const startIdx = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const endIdx = Math.min(page * PAGE_SIZE, totalCount)

  const toggleColumn = (columnId: string) => {
    const key = columnId as ColumnKey
    if (!ALL_COLUMNS.some((c) => c.key === key)) return
    setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const columnMenuItems = useMemo(
    () => ALL_COLUMNS.map((c) => ({ id: c.key, label: t(c.labelKey) })),
    [t],
  )

  const handleAdd = () => navigate('/inventory/movements/new')
  const handleView = (row: InventoryMovementRow) =>
    navigate(`/inventory/movements/${row.id}/edit?view=1`, { state: { movement: row } })

  const handleCopy = () => {
    if (filtered.length === 0) {
      toast.error(t('inventoryMovements.nothingToCopy'))
      return
    }

    const header = [
      t('inventoryMovements.columns.code'),
      t('inventoryMovements.columns.movementDate'),
      t('inventoryMovements.columns.remarks'),
      t('inventoryMovements.columns.sourceWarehouse'),
      t('inventoryMovements.columns.destinationWarehouse'),
    ]
    const lines = filtered.map((r) => [r.code, r.movementDate, r.remarks, r.sourceWarehouse, r.destinationWarehouse].join('\t'))
    void navigator.clipboard.writeText([header.join('\t'), ...lines].join('\n'))
    toast.success(t('inventoryMovements.copied'))
  }

  const handleExportCSV = () => {
    if (filtered.length === 0) {
      toast.error(t('inventoryMovements.nothingToExport'))
      return
    }
    const header = [
      t('inventoryMovements.columns.code'),
      t('inventoryMovements.columns.movementDate'),
      t('inventoryMovements.columns.remarks'),
      t('inventoryMovements.columns.sourceWarehouse'),
      t('inventoryMovements.columns.destinationWarehouse'),
    ].join(',')
    const body = filtered
      .map((r) =>
        [r.code, r.movementDate, r.remarks, r.sourceWarehouse, r.destinationWarehouse]
          .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
          .join(','),
      )
      .join('\n')
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `inventory_movements_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success(t('inventoryMovements.exported'))
  }

  const visibleCount = Object.values(visibleColumns).filter(Boolean).length

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('inventoryMovements.title')}</h1>
            <p className="text-muted-foreground text-sm">{t('inventoryMovements.listSubtitle')}</p>
          </div>
        </div>

        <Button onClick={handleAdd} className="w-full sm:w-auto shrink-0">
          <Plus className="mr-2 h-4 w-4" />
          {t('inventoryMovements.addMovement')}
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
            <div className="flex-1">
              <label className="mb-2 block text-sm font-medium">{t('inventoryMovements.search')}</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t('inventoryMovements.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value)
                    setPageNumber(1)
                  }}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <TableColumnVisibilityMenu
                columns={columnMenuItems}
                visibility={visibleColumns}
                onToggle={toggleColumn}
                triggerLabel={t('inventoryMovements.columnVisibility')}
                menuTitle={t('inventoryMovements.columnsToggle')}
              />

              <Button variant="outline" size="sm" onClick={handleExportCSV}>
                <FileDown className="mr-2 h-4 w-4" />
                CSV
              </Button>
              <Button variant="outline" size="sm" onClick={handleCopy}>
                <Copy className="mr-2 h-4 w-4" />
                {t('inventoryMovements.copy')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                  {visibleColumns.code && <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">{t('inventoryMovements.columns.code')}</TableHead>}
                  {visibleColumns.movementDate && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('inventoryMovements.columns.movementDate')}
                    </TableHead>
                  )}
                  {visibleColumns.remarks && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('inventoryMovements.columns.remarks')}
                    </TableHead>
                  )}
                  {visibleColumns.sourceWarehouse && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('inventoryMovements.columns.sourceWarehouse')}
                    </TableHead>
                  )}
                  {visibleColumns.destinationWarehouse && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('inventoryMovements.columns.destinationWarehouse')}
                    </TableHead>
                  )}
                  <TableHead className="h-12 w-[88px] bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                    {t('inventoryMovements.columns.view')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={visibleCount + 1} className="p-0">
                      <ListLoader message={t('common.loading', { defaultValue: 'Loading...' })} />
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleCount + 1} className="py-12 text-center text-muted-foreground">
                      {t('inventoryMovements.emptyTable')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((row) => {
                    return (
                      <TableRow key={row.id} className="border-b transition-colors hover:bg-primary/5 dark:hover:bg-primary/10">
                        {visibleColumns.code && <TableCell className="px-4 py-2 text-center text-sm">{row.code}</TableCell>}
                        {visibleColumns.movementDate && (
                          <TableCell className="px-4 py-2 text-center text-sm">{row.movementDate ? String(row.movementDate).slice(0, 10) : '—'}</TableCell>
                        )}
                        {visibleColumns.remarks && (
                          <TableCell className="px-4 py-2 text-center text-sm">{row.remarks || '—'}</TableCell>
                        )}
                        {visibleColumns.sourceWarehouse && (
                          <TableCell className="px-4 py-2 text-center text-sm">{row.sourceWarehouse}</TableCell>
                        )}
                        {visibleColumns.destinationWarehouse && (
                          <TableCell className="px-4 py-2 text-center text-sm">{row.destinationWarehouse}</TableCell>
                        )}
                        <TableCell className="px-2 py-2 text-center">
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleView(row)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-col items-center justify-between gap-4 border-t px-4 py-4 sm:flex-row sm:px-6">
            <p className="text-center text-sm text-muted-foreground sm:text-left">
              {t('inventoryMovements.showing', { start: startIdx, end: endIdx, total: totalCount })}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPageNumber((p) => Math.max(1, p - 1))} disabled={page <= 1}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="min-w-[2rem] text-center text-sm font-medium">{page}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

